"use client";

import React from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, A11y } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";

import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { ImageSliderFields } from "@/types/acf";

function ArrowButton({
  direction,
  ariaLabel,
  onClick,
}: {
  direction: "prev" | "next";
  ariaLabel: string;
  onClick: () => void;
}) {
  const isPrev = direction === "prev";

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="group inline-flex items-center justify-center w-10 h-10 text-fuji-black hover:opacity-70 transition-opacity duration-200"
    >
      <span
        className={[
          "inline-flex items-center justify-center",
          "transform-gpu transition-transform duration-300",
          "[transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]",
          isPrev
            ? "rotate-180"
            : "",
        ].join(" ")}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 19"
          fill="none"
          aria-hidden="true"
          className="stroke-current"
        >
          <path
            d="M1 9.5H17M17 9.5L11.5 4M17 9.5L11.5 15"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

type Props = {
  data: ImageSliderFields & { id?: string; hide?: boolean };
};

export default function ImageSlider({ data }: Props) {
  const swiperRef = React.useRef<SwiperType | null>(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const images = Array.isArray(data?.images) ? data.images : [];
  const slides: { src: string; alt: string }[] = [];

  for (const item of images) {
    const img = item?.image;
    const src = img?.url ? toAbsoluteUrl(img.url) : "";
    if (!src) continue;

    slides.push({
      src,
      alt: img?.alt || "Slide image",
    });
  }

  if (!data || data.hide) return null;
  if (slides.length === 0) return null;

  return (
    <div id={data.id} className="min-w-0">
      {!mounted ? (
        <div className="w-full bg-fuji-grey">
          <div className="relative w-full" style={{ aspectRatio: "3 / 2" }}>
            <Image
              src={slides[0].src}
              alt={slides[0].alt}
              fill
              className="object-cover"
              sizes="(min-width: 1024px) 50vw, 100vw"
              quality={96}
            />
          </div>
        </div>
      ) : (
        <>
          <Swiper
            className="w-full min-w-0"
            modules={[Pagination, A11y]}
            slidesPerView={1}
            loop={slides.length > 1}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            pagination={{
              clickable: true,
              el: ".image-slider-pagination",
              bulletClass:
                "inline-block w-2 h-2 rounded-full bg-fuji-black/30 opacity-100",
              bulletActiveClass: "!bg-fuji-black",
            }}
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={index}>
                <div className="w-full bg-fuji-grey">
                  <div
                    className="relative w-full"
                    style={{ aspectRatio: "3 / 2" }}
                  >
                    <Image
                      src={slide.src}
                      alt={slide.alt}
                      fill
                      className="object-cover"
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      quality={90}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {slides.length > 1 && (
            <div className="grid grid-cols-[auto_auto_auto] items-center justify-center gap-4">
              <ArrowButton
                direction="prev"
                ariaLabel="Previous slide"
                onClick={() => swiperRef.current?.slidePrev()}
              />

              <div className="image-slider-pagination flex items-center justify-center gap-2" />

              <ArrowButton
                direction="next"
                ariaLabel="Next slide"
                onClick={() => swiperRef.current?.slideNext()}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
